import type {CSSProperties} from 'react';
import {colorToHexString} from '@Utils/color.util';
import {getBrandFontForType, getBrandKitDisplayName, getBrandLogoImageUrl} from '@Libraries/brands-library';
import type {Brand, BrandColor, BrandContent, BrandFont, BrandLogo, BrandsAppState} from '@Components/mystuff-brands/brand-kits.types';
import {BrandAssetName, FontType} from '@Components/mystuff-brands/brand-kits.types';
import type {IconsOnlyControl} from '@Components/input-field-v2/input-field.types';
import type {BrandKitPillCluster, KitSearchSlotClassNames} from './brand-kit-pill-row.types';
import {KIT_PILL_SWATCH_COUNT, OVERFLOW_SEARCH_CLEAR_CONTROL, OVERFLOW_SEARCH_ICON_CONTROL} from './brand-kit-pill-row.types';

/**
 * The hidden-kits search field's one control: the search icon while the field is empty, a clear button once
 * something is typed. Each surface hands in its slot classes, which keep the slot from covering the field's border.
 */
export const getKitSearchControl = (searchTerm: string, slotClassNames: KitSearchSlotClassNames): IconsOnlyControl => {
  if (searchTerm === '') {
    return {...OVERFLOW_SEARCH_ICON_CONTROL, containerClassName: slotClassNames.searchIconSlot};
  }

  return {...OVERFLOW_SEARCH_CLEAR_CONTROL, containerClassName: slotClassNames.clearSlot};
};

export const isKitSearchClearControl = (controlId: string): boolean => {
  return controlId === OVERFLOW_SEARCH_CLEAR_CONTROL.id;
};

/**
 * The circles on a kit's pill: its first logo then its first colours, three in total.
 * Reads the store maps directly rather than resolving the whole kit, since a pill only needs a logo URL and a few hex codes.
 */
export const getClusterForKit = (brandId: string, brandContent: BrandContent, state: Pick<BrandsAppState, 'colors' | 'logos'>): BrandKitPillCluster => {
  const logoSrc = getFirstLogoSrcForKit(brandId, brandContent, state.logos);
  const colorCount = logoSrc === null ? KIT_PILL_SWATCH_COUNT : KIT_PILL_SWATCH_COUNT - 1;

  return {logoSrc, hexCodes: getSwatchHexCodesForKit(brandId, brandContent, state.colors, colorCount)};
};

/**
 * The "+N more" pill's cluster stands for the kits it hides: the first logo among them, then one
 * colour from each of the rest, capped at the usual three.
 */
export const getClusterForHiddenKits = (brandIds: string[], brandContent: BrandContent, state: Pick<BrandsAppState, 'colors' | 'logos'>): BrandKitPillCluster => {
  const logoSrc =
    brandIds
      .map((brandId) => {
        return getFirstLogoSrcForKit(brandId, brandContent, state.logos);
      })
      .find((src) => {
        return src !== null;
      }) ?? null;
  const colorCount = logoSrc === null ? KIT_PILL_SWATCH_COUNT : KIT_PILL_SWATCH_COUNT - 1;
  const hexCodes = brandIds.flatMap((brandId) => {
    return getSwatchHexCodesForKit(brandId, brandContent, state.colors, 1);
  });

  return {logoSrc, hexCodes: Array.from(new Set(hexCodes)).slice(0, colorCount)};
};

/**
 * Sets a kit's name in the kit's own heading font. The typography utility classes fix size, weight
 * and line-height but leave the family alone, so it is applied on a wrapper and inherited.
 */
export const getKitNameFontStyle = (headingFontFamily: string | null): CSSProperties => {
  return headingFontFamily === null ? {} : {fontFamily: `"${headingFontFamily}", inherit`};
};

const getFirstLogoSrcForKit = (brandId: string, brandContent: BrandContent, logos: BrandsAppState['logos']): string | null => {
  const firstLogoId = brandContent[brandId]?.[BrandAssetName.LOGOS].ids[0];

  if (firstLogoId === undefined) {
    return null;
  }

  const logo: BrandLogo | undefined = logos[firstLogoId];

  // A logo's `src` is the upload source it came through ("upload"), not a URL.
  return logo ? getBrandLogoImageUrl(logo) : null;
};

const getSwatchHexCodesForKit = (brandId: string, brandContent: BrandContent, colors: BrandsAppState['colors'], colorCount: number): string[] => {
  const colorIds = brandContent[brandId]?.[BrandAssetName.COLORS].ids ?? [];

  return colorIds
    .slice(0, colorCount)
    .map((colorId) => {
      return colors[colorId];
    })
    .filter((color): color is BrandColor => {
      return Boolean(color);
    })
    .map((color) => {
      return colorToHexString(color.hexCode);
    });
};

/**
 * The kit's heading typeface, so a pill can be set in the kit's own font rather than the UI face.
 * Returns null for a kit with no heading font, which leaves the pill on the UI font.
 */
export const getHeadingFontFamilyForKit = (brandId: string, brandContent: BrandContent, fonts: BrandsAppState['fonts']): string | null => {
  const fontIds = brandContent[brandId]?.[BrandAssetName.FONTS].ids ?? [];
  const kitFonts = fontIds
    .map((fontId) => {
      return fonts[fontId];
    })
    .filter((font): font is BrandFont => {
      return Boolean(font);
    });
  const headingFont = getBrandFontForType(kitFonts, FontType.HEADING);

  return headingFont?.fontFamily ?? null;
};

/**
 * Every input to a pill's width: which kits there are, their names, default flag and swatches, and the active
 * kit. The swatches count because a kit's logo and colours load after its pill is first fitted.
 */
export const getKitRowLayoutSignature = (brandKits: Brand[], activeBrandId: string, getClusterForBrandId: (brandId: string) => BrandKitPillCluster): string => {
  const kitSignatures = brandKits.map((brandKit) => {
    const cluster = getClusterForBrandId(brandKit.brandId);

    return `${brandKit.brandId}:${getBrandKitDisplayName(brandKit)}:${String(brandKit.isDefault)}:${cluster.logoSrc ?? ''}:${cluster.hexCodes.join(',')}`;
  });

  return `${activeBrandId}|${kitSignatures.join('|')}`;
};
